'use client';

import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { Explain } from '@flipster/react/design-utils/explain';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';
import { Slider } from '@flipster/ui';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';

import { NumberInputWithSuffix } from '../../input/number-input-with-suffix';
import { positionCloseModalBunja } from '../state';

const PercentMarks = [
  { label: '0%', value: 0 },
  { label: '25%', value: 25 },
  { label: '50%', value: 50 },
  { label: '75%', value: 75 },
  { label: '100%', value: 100 },
];

export const QtySlider = () => {
  const t = useTranslations();
  const { positionAtom, inputQtyAtom, percentAtom, amountAtom, instrumentAtom } = useBunja(positionCloseModalBunja);
  const [inputQty, setInpuQty] = useAtom(inputQtyAtom);

  const position = useAtomValue(positionAtom);
  const percent = useAtomValue(percentAtom);
  const amount = useAtomValue(amountAtom);
  const instrument = useAtomValue(instrumentAtom);

  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);

  const minAmount = Num(instrument.notionalMinOrderAmount)
    .dividedBy(position.side === 'Long' ? position.bidPrice : position.askPrice)
    .toFixed(instrument.tradeQtyPrecision, BigNumber.ROUND_UP);
  const isNotEnough = Num(inputQty).isLessThan(minAmount);

  const handleChangeSlider = (sliderPercent: number) => {
    const newQty = Num(position.quantity)
      .abs()
      .multipliedBy(sliderPercent)
      .dividedBy(100)
      .toFixed(instrument.tradeQtyPrecision);

    setInpuQty(newQty);
  };

  return (
    <div
      className={stack({
        py: 12,
        px: 16,
        bg: 'background.default.level-1',
        borderRadius: 8,
        gap: 16,
      })}
    >
      <div>
        <div className={css({ textStyle: 'Text/M/Strong', mb: 8 })}>{t('Position_close.r250401.Close_quantity')}</div>
        <div className={flex({ justify: 'space-between', align: 'flex-end' })}>
          <div>
            <NumberInputWithSuffix
              value={inputQty}
              max={Num(position.quantity).abs().toString()}
              min={minAmount}
              className={flex({
                textStyle: 'Title/M/Monospace',
                color: 'content.default.level-5',
                align: 'flex-end',
                gap: 4,
              })}
              onChange={(value) => setInpuQty(value || '0')}
              precision={instrument.tradeQtyPrecision}
              suffix={<span className={css({ textStyle: 'Text/L/Monospace/Strong' })}>{instrument.currency}</span>}
            />
            <div>
              <Formatted.Number
                prefix='≈ '
                value={amount}
                suffix={instrument.quoteCurrency}
                precision={quoteCurrencyPrecision}
                abs
                withComma
                css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-2' }}
              />
            </div>
          </div>
          <span
            className={css({
              display: 'inline-block',
              textStyle: 'Text/S/Monospace/Strong',
              p: 4,
              borderRadius: 4,
              bg: 'background.default.level-3',
            })}
          >
            <Formatted.Percent value={percent} />
          </span>
        </div>
      </div>
      {isNotEnough && (
        <Explain.Warning css={{ '& svg': { display: 'none' } }}>
          {t('Trade.r251119.Minimum_closable_amount_symbol_is_amount', {
            symbol: instrument.currency,
            amount: numberFormatter(minAmount, { precision: instrument.tradeQtyPrecision, withComma: true }),
          })}
        </Explain.Warning>
      )}

      <Slider min={1} max={100} value={percent} marks={PercentMarks} onChange={handleChangeSlider} />
    </div>
  );
};
